import { useState } from "react"
import { LogOut, Settings } from "lucide-react"
import { useAuth } from "@/core/hooks/useAuth"
import { useUserPreferences } from "@/features/dashboard/hooks/useDashboard"
import { Button } from "@/shared/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu"
import { Avatar, AvatarFallback } from "@/shared/components/ui/avatar"
import UserSettingsDialog from "@/shared/components/UserSettingsDialog/UserSettingsDialog"

export default function UserProfile() {
  const { account, logout } = useAuth();
  const { preferences, refetch: refetchPrefs } = useUserPreferences();
  const [settingsOpen, setSettingsOpen] = useState(false);

  if (!account) return null;

  const initials = account.name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase() || 'U';

  const dashboardPrefs = preferences?.dashboard_preferences ?? {
    enabled: false,
    show_recent_changes: true,
    show_statistics: true,
    recent_changes_days: 7,
    enable_analytics: true,
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="relative h-10 w-10 rounded-full">
            <Avatar>
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56" align="end" forceMount>
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium leading-none">{account.name}</p>
              <p className="text-xs leading-none text-muted-foreground">
                {account.username}
              </p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setSettingsOpen(true)}>
            <Settings className="mr-2 h-4 w-4" />
            <span>Settings</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logout}>
            <LogOut className="mr-2 h-4 w-4" />
            <span>Sign out</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <UserSettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        dashboardPreferences={dashboardPrefs}
        onSaved={refetchPrefs}
      />
    </>
  );
}
